import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BytesPipe } from '../../../pipes/bytes.pipe';

type ViewerType = 'boolean' | 'date' | 'datetime' | 'number' | 'bytes' | 'list' | 'object' | 'objects' | 'json' | 'text';

interface ViewerRow {
  key: string;
  type: ViewerType;
  value: any;
}

/** keys whose numeric value is an identifier, not a quantity, so it keeps its raw form */
const RAW_NUMBER_KEY = /(^|_)(ids|year|port|ports|code|zip|postal|serial|mac|ip|phone|mobile|tel|national|account|card|pin|otp|vlan|no|number)(_|$)/i;
/** id, user_id, radacctid, pid: anything ending in id is a key, not a quantity */
const ID_KEY = /id$/i;
/** ... except these, which merely end in the same two letters */
const NOT_ID_KEY = /(^|_)((un|pre|over)?paid|(in)?valid)$/i;
/** radius-style traffic counters (acctinputoctets, freeoctets, ...) are byte totals */
const OCTETS_KEY = /octets?/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?/;
const NUMERIC = /^-?\d+(\.\d+)?$/;
/** nesting past this depth is dumped as json, so a cyclic payload cannot recurse forever */
const MAX_DEPTH = 4;

@Component({
  selector: 'app-object-viewer',
  templateUrl: './object-viewer.component.html',
  styleUrls: ['./object-viewer.component.scss']
})
export class ObjectViewerComponent implements OnInit, OnDestroy {
  @Input() dir;
  @Input() title;
  /** set by the component on itself when rendering a nested value: drops the modal chrome */
  @Input() nested = false;
  @Input() depth = 0;

  rows: ViewerRow[] = [];

  private source: any;
  private bytesPipe = new BytesPipe();
  private copyFlashTarget: HTMLElement = null;
  private copyFlashTitle: string = null;
  private copyFlashTimer: any = null;

  constructor(public translator: TranslateService) { }

  @Input()
  set obj(value: any) {
    this.source = value;
    this.rows = this.build(value);
  }

  get obj() {
    return this.source;
  }

  // the modal callers assign obj imperatively, template nesting binds it: rebuild here so the
  // rows are typed once every input (depth included) has landed, whichever way obj arrived
  ngOnInit() {
    this.rows = this.build(this.source);
  }

  get lang() {
    return this.translator.currentLang || 'fa';
  }

  ngOnDestroy() {
    if (this.copyFlashTimer) { clearTimeout(this.copyFlashTimer); }
  }

  /** the checkbox and the nested viewers below carry their own copy targets */
  canCopy(row: ViewerRow) {
    return row.type !== 'boolean' && row.type !== 'object' && row.type !== 'objects';
  }

  /**
   * Copy a value on click, reading the cell's rendered text so the jalali date,
   * byte size or comma-grouped number reaches the clipboard as it is shown.
   *
   * navigator.clipboard is only defined in a secure context, which holds over
   * https but not when the app is opened over plain http on a LAN address, so
   * fall back to a detached textarea + execCommand there (as base-list does).
   */
  async copyValue(event: MouseEvent) {
    // a nested viewer's cell is inside the parent's cell: copy only the inner one
    event.stopPropagation();
    // Someone dragging to select part of a long value ends on a click here;
    // copying the whole cell would silently replace what they just selected.
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) { return; }
    const cell = event.currentTarget as HTMLElement;
    const text = (cell.innerText || '').replace(/\s+/g, ' ').trim();
    if (!text) { return; }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.top = '-1000px';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      this.flashCopied(cell);
    } catch (error) {
      // a blocked clipboard leaves the cell unflashed, which is the whole report
    }
  }

  /**
   * Confirm the copy on the cell that was clicked: `.copy-done` swaps the icon
   * to a green check for a moment. A toast for something this small is more
   * interruption than it is worth inside a modal this dense.
   */
  private flashCopied(element: HTMLElement) {
    // copying a second value while the first check is up: drop the old one now,
    // or its timer would strip the class off the new element early
    if (this.copyFlashTimer) {
      clearTimeout(this.copyFlashTimer);
      this.clearCopyFlash();
    }
    this.copyFlashTarget = element;
    this.copyFlashTitle = element.getAttribute('title');
    element.classList.add('copy-done');
    // the green check is the only cue otherwise, and colour alone is not one
    element.setAttribute('title', this.translator.instant('COPIED'));
    this.copyFlashTimer = setTimeout(() => {
      this.clearCopyFlash();
      this.copyFlashTimer = null;
    }, 1200);
  }

  private clearCopyFlash() {
    const element = this.copyFlashTarget;
    if (!element) { return; }
    element.classList.remove('copy-done');
    if (this.copyFlashTitle === null) {
      element.removeAttribute('title');
    } else {
      element.setAttribute('title', this.copyFlashTitle);
    }
    this.copyFlashTarget = null;
    this.copyFlashTitle = null;
  }

  private build(obj: any): ViewerRow[] {
    if (!obj || typeof obj !== 'object') { return []; }
    return Object.keys(obj)
      .filter(k => this.hasValue(obj[k]))
      .map(k => this.row(k, obj[k]));
  }

  /** anything falsy (null, '', 0, false) is left out, as is an empty array or object */
  private hasValue(value: any) {
    if (!value) { return false; }
    if (value instanceof Date) { return true; }
    if (typeof value === 'object') { return Object.keys(value).length > 0; }
    return true;
  }

  private row(key: string, value: any): ViewerRow {
    if (typeof value === 'boolean' || value === 'true' || value === 'false') {
      return { key, type: 'boolean', value: value === true || value === 'true' };
    }
    if (value instanceof Date || DATE_TIME.test(value)) {
      return { key, type: 'datetime', value };
    }
    if (DATE_ONLY.test(value)) {
      return { key, type: 'date', value };
    }
    if (OCTETS_KEY.test(key) && this.isNumeric(value)) {
      return { key, type: 'bytes', value: this.toBytes(value) };
    }
    if (this.isNumber(key, value)) {
      return { key, type: 'number', value: this.addCommas(value) };
    }
    if (Array.isArray(value)) {
      if (value.every(i => i == null || typeof i !== 'object')) {
        return { key, type: 'list', value: value.map(i => this.simple(key, i)).join(this.lang === 'fa' ? '، ' : ', ') };
      }
      return this.deep(key, 'objects', value.filter(i => i != null));
    }
    if (typeof value === 'object') {
      return this.deep(key, 'object', value);
    }
    return { key, type: 'text', value };
  }

  /** hands a nested value back to the component itself, unless it is too deep to keep unfolding */
  private deep(key: string, type: ViewerType, value: any): ViewerRow {
    if (this.depth < MAX_DEPTH) { return { key, type, value }; }
    return { key, type: 'json', value: this.stringify(value) };
  }

  private stringify(value: any) {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      // circular payload
      return '' + value;
    }
  }

  /** formats a primitive array item the same way a scalar value would be shown */
  private simple(key: string, value: any) {
    if (typeof value === 'boolean') { return this.translator.instant(value ? 'YES' : 'NO'); }
    if (OCTETS_KEY.test(key) && this.isNumeric(value)) { return this.toBytes(value); }
    if (this.isNumber(key, value)) { return this.addCommas(value); }
    return value;
  }

  private toBytes(value: any) {
    return this.bytesPipe.transform(+value, 2, 'B', this.lang === 'fa' ? 'fa' : 'en');
  }

  private isNumber(key: string, value: any) {
    return !this.isIdentifier(key) && this.isNumeric(value);
  }

  private isIdentifier(key: string) {
    if (RAW_NUMBER_KEY.test(key)) { return true; }
    return ID_KEY.test(key) && !NOT_ID_KEY.test(key);
  }

  private isNumeric(value: any) {
    if (typeof value === 'number') { return isFinite(value); }
    // a leading zero means the digits are a code, not a quantity
    return typeof value === 'string' && NUMERIC.test(value) && !/^-?0\d/.test(value);
  }

  private addCommas(value: any) {
    const parts = ('' + value).split('.');
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(parts[0])) {
      parts[0] = parts[0].replace(rgx, '$1,$2');
    }
    return parts.join('.');
  }
}
